import { getApiError } from '../lib/apiError';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { 
  FileText, Settings, Upload, Shield, CheckCircle, AlertCircle,
  Loader2, Building2, Key, Server, FileCode, Download, Send, RefreshCw, Wifi, WifiOff, Trash2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NFCeConfig = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingCert, setUploadingCert] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [cleaningRejected, setCleaningRejected] = useState(false);
  const [sefazStatus, setSefazStatus] = useState(null);
  const [sendingNfce, setSendingNfce] = useState({});
  const [config, setConfig] = useState({
    cnpj: '',
    inscricao_estadual: '',
    razao_social: '',
    nome_fantasia: '',
    endereco: '',
    numero: '',
    bairro: '',
    cidade: '',
    uf: 'AM',
    cep: '',
    codigo_municipio: '',
    telefone: '',
    csc_id: '',
    csc_token: '',
    ambiente: 1,
    serie_nfce: 1,
    numero_nfce: 1,
    regime_tributario: 1,
    incluir_ibs_cbs: 'auto'
  });
  const [certFile, setCertFile] = useState(null);
  const [certPassword, setCertPassword] = useState('');
  const [certInfo, setCertInfo] = useState(null);
  const [nfces, setNfces] = useState([]);
  const [loadingNfces, setLoadingNfces] = useState(false);

  useEffect(() => {
    fetchConfig();
    fetchNfces();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/nfce/config`);
      if (response.data.configured) {
        setConfig(prev => ({ ...prev, ...response.data }));
        if (response.data.certificate_info) {
          setCertInfo(response.data.certificate_info);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar configuração:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNfces = async () => {
    setLoadingNfces(true);
    try {
      const response = await axios.get(`${API_URL}/api/nfce?limit=20`);
      setNfces(response.data);
    } catch (error) {
      console.error('Erro ao carregar NFC-es:', error);
    } finally {
      setLoadingNfces(false);
    }
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/nfce/config`, config);
      toast.success('Configuração salva com sucesso!');
    } catch (error) {
      toast.error(getApiError(error, 'Erro ao salvar configuração'));
    } finally {
      setSaving(false);
    }
  };

  const handleUploadCertificate = async () => {
    if (!certFile) {
      toast.error('Selecione um arquivo de certificado');
      return;
    }
    if (!certPassword) {
      toast.error('Informe a senha do certificado');
      return;
    }

    setUploadingCert(true);
    try {
      const formData = new FormData();
      formData.append('file', certFile);
      formData.append('senha', certPassword);

      const response = await axios.post(`${API_URL}/api/nfce/certificate`, formData);
      toast.success('Certificado enviado com sucesso!');
      setCertInfo(response.data.certificate_info);
      setCertFile(null);
      setCertPassword('');
    } catch (error) {
      toast.error(getApiError(error, 'Erro ao enviar certificado'));
    } finally {
      setUploadingCert(false);
    }
  };

  const handleCheckSefazStatus = async () => {
    setCheckingStatus(true);
    try {
      const response = await axios.get(`${API_URL}/api/nfce/status-sefaz`);
      setSefazStatus(response.data);
      if (response.data.online) {
        toast.success('SEFAZ online e operacional!');
      } else {
        toast.error(`SEFAZ offline: ${response.data.motivo || response.data.erro}`);
      }
    } catch (error) {
      toast.error(getApiError(error, 'Erro ao verificar status da SEFAZ'));
      setSefazStatus({ online: false, erro: getApiError(error, 'Erro ao consultar SEFAZ') });
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleCleanRejected = async () => {
    if (!window.confirm('Tem certeza? Isso removerá todas as NFC-e rejeitadas e ajustará o próximo número.')) return;
    setCleaningRejected(true);
    try {
      const response = await axios.post(`${API_URL}/api/nfce/limpar-rejeitadas`);
      toast.success(response.data.message);
      setConfig(prev => ({ ...prev, numero_nfce: response.data.proximo_numero }));
    } catch (error) {
      toast.error(getApiError(error, 'Erro ao limpar rejeitadas'));
    } finally {
      setCleaningRejected(false);
    }
  };


  const handleSendNfce = async (nfceId) => {
    setSendingNfce(prev => ({ ...prev, [nfceId]: true }));
    try {
      const response = await axios.post(`${API_URL}/api/nfce/${nfceId}/send`);
      
      if (response.data.success) {
        toast.success(`NFC-e autorizada! Protocolo: ${response.data.protocolo}`);
      } else {
        toast.error(`NFC-e rejeitada: ${response.data.motivo}`);
      }
      
      // Atualiza a lista
      fetchNfces();
    } catch (error) {
      toast.error(getApiError(error, 'Erro ao enviar NFC-e para SEFAZ'));
    } finally {
      setSendingNfce(prev => ({ ...prev, [nfceId]: false }));
    }
  };

  const handleDownloadXml = async (nfceId, chave) => {
    try {
      const response = await axios.get(`${API_URL}/api/nfce/${nfceId}/xml`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `nfce_${chave}.xml`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('XML baixado!');
    } catch (error) {
      toast.error('Erro ao baixar XML');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('pt-BR');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-blue-600" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="nfce-config-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Configuração NFC-e</h1>
          <p className="text-slate-500">Configure a emissão de Nota Fiscal de Consumidor Eletrônica</p>
        </div>
        <Badge variant={config.ambiente === 1 ? 'default' : 'secondary'} className="text-sm">
          {config.ambiente === 1 ? 'Produção' : 'Homologação'}
        </Badge>
      </div>

      <Tabs defaultValue="empresa" className="space-y-4">
        <TabsList>
          <TabsTrigger value="empresa" className="gap-2">
            <Building2 size={16} />
            Dados da Empresa
          </TabsTrigger>
          <TabsTrigger value="certificado" className="gap-2">
            <Shield size={16} />
            Certificado Digital
          </TabsTrigger>
          <TabsTrigger value="sefaz" className="gap-2">
            <Server size={16} />
            SEFAZ
          </TabsTrigger>
          <TabsTrigger value="notas" className="gap-2">
            <FileText size={16} />
            Notas Emitidas
          </TabsTrigger>
        </TabsList>

        {/* Dados da Empresa */}
        <TabsContent value="empresa">
          <Card>
            <CardHeader>
              <CardTitle>Dados do Emitente</CardTitle>
              <CardDescription>Informações da empresa para emissão de NFC-e</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>CNPJ *</Label>
                  <Input
                    value={config.cnpj}
                    onChange={(e) => setConfig({ ...config, cnpj: e.target.value })}
                    placeholder="00.000.000/0000-00"
                    data-testid="nfce-cnpj"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Inscrição Estadual *</Label>
                  <Input
                    value={config.inscricao_estadual}
                    onChange={(e) => setConfig({ ...config, inscricao_estadual: e.target.value })}
                    placeholder="000000000"
                    data-testid="nfce-ie"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Razão Social *</Label>
                  <Input
                    value={config.razao_social}
                    onChange={(e) => setConfig({ ...config, razao_social: e.target.value })}
                    placeholder="Razão social da empresa"
                    data-testid="nfce-razao"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nome Fantasia</Label>
                  <Input
                    value={config.nome_fantasia}
                    onChange={(e) => setConfig({ ...config, nome_fantasia: e.target.value })}
                    placeholder="Nome fantasia"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2 md:col-span-2">
                  <Label>Endereço *</Label>
                  <Input
                    value={config.endereco}
                    onChange={(e) => setConfig({ ...config, endereco: e.target.value })}
                    placeholder="Rua, Avenida..."
                  />
                </div>
                <div className="space-y-2">
                  <Label>Número *</Label>
                  <Input
                    value={config.numero}
                    onChange={(e) => setConfig({ ...config, numero: e.target.value })}
                    placeholder="Nº"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Bairro *</Label>
                  <Input
                    value={config.bairro}
                    onChange={(e) => setConfig({ ...config, bairro: e.target.value })}
                    placeholder="Bairro"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Cidade *</Label>
                  <Input
                    value={config.cidade}
                    onChange={(e) => setConfig({ ...config, cidade: e.target.value })}
                    placeholder="Cidade"
                  />
                </div>
                <div className="space-y-2">
                  <Label>UF *</Label>
                  <Input
                    value={config.uf}
                    onChange={(e) => setConfig({ ...config, uf: e.target.value.toUpperCase() })}
                    placeholder="AM"
                    maxLength={2}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>CEP *</Label>
                  <Input
                    value={config.cep}
                    onChange={(e) => setConfig({ ...config, cep: e.target.value })}
                    placeholder="00000-000"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Código Município (IBGE) *</Label>
                  <Input
                    value={config.codigo_municipio}
                    onChange={(e) => setConfig({ ...config, codigo_municipio: e.target.value })}
                    placeholder="1302603 (Manaus)"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Telefone</Label>
                  <Input
                    value={config.telefone}
                    onChange={(e) => setConfig({ ...config, telefone: e.target.value })}
                    placeholder="(92) 99999-9999"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Regime Tributário *</Label>
                <Select
                  value={String(config.regime_tributario)}
                  onValueChange={(v) => setConfig({ ...config, regime_tributario: parseInt(v) })}
                >
                  <SelectTrigger data-testid="nfce-regime">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Simples Nacional</SelectItem>
                    <SelectItem value="2">Simples Nacional - Excesso</SelectItem>
                    <SelectItem value="3">Regime Normal</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>IBS/CBS — Reforma Tributária (NT 2025.002)</Label>
                <Select
                  value={config.incluir_ibs_cbs || 'auto'}
                  onValueChange={(v) => setConfig({ ...config, incluir_ibs_cbs: v })}
                >
                  <SelectTrigger data-testid="nfce-ibs-cbs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Automático (recomendado) — inclui apenas p/ Regime Normal em 2026</SelectItem>
                    <SelectItem value="sempre">Sempre incluir IBS/CBS no XML</SelectItem>
                    <SelectItem value="nunca">Nunca incluir</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  Simples Nacional: exigência prevista para 2027. Se a SEFAZ rejeitar com "cStat 1115 - IBS/CBS não informado", mude para "Sempre incluir".
                </p>
              </div>

              <Button onClick={handleSaveConfig} disabled={saving} className="gap-2">
                {saving ? <Loader2 className="animate-spin" size={16} /> : <Settings size={16} />}
                Salvar Configurações
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Certificado Digital */}
        <TabsContent value="certificado">
          <Card>
            <CardHeader>
              <CardTitle>Certificado Digital A1</CardTitle>
              <CardDescription>Certificado necessário para assinar as notas fiscais</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {certInfo && (
                <Alert className="border-green-200 bg-green-50">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <AlertTitle className="text-green-800">Certificado Instalado</AlertTitle>
                  <AlertDescription className="text-green-700">
                    <p><strong>Titular:</strong> {certInfo.cn}</p>
                    <p><strong>Válido até:</strong> {certInfo.valid_until}</p>
                  </AlertDescription>
                </Alert>
              )}

              <div className="space-y-4 p-4 bg-slate-50 rounded-lg border">
                <div className="space-y-2">
                  <Label>Arquivo do Certificado (.pfx ou .p12)</Label>
                  <Input
                    type="file"
                    accept=".pfx,.p12"
                    onChange={(e) => setCertFile(e.target.files[0])}
                    className="cursor-pointer"
                    data-testid="nfce-cert-file"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Senha do Certificado</Label>
                  <Input
                    type="password"
                    value={certPassword}
                    onChange={(e) => setCertPassword(e.target.value)}
                    placeholder="Digite a senha do certificado"
                    data-testid="nfce-cert-password"
                  />
                </div>

                <Button 
                  onClick={handleUploadCertificate} 
                  disabled={uploadingCert || !certFile}
                  className="gap-2"
                >
                  {uploadingCert ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />}
                  Enviar Certificado
                </Button>
              </div>

              <Alert>
                <Shield className="h-5 w-5" />
                <AlertTitle>Segurança</AlertTitle>
                <AlertDescription>
                  O certificado digital é armazenado de forma segura e é utilizado apenas para 
                  assinatura das notas fiscais. Mantenha a senha em local seguro.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Configurações SEFAZ */}
        <TabsContent value="sefaz">
          <Card>
            <CardHeader>
              <CardTitle>Configurações SEFAZ</CardTitle>
              <CardDescription>Parâmetros de comunicação com a Secretaria da Fazenda</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Ambiente *</Label>
                  <Select
                    value={String(config.ambiente)}
                    onValueChange={(v) => setConfig({ ...config, ambiente: parseInt(v) })}
                  >
                    <SelectTrigger data-testid="nfce-ambiente">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Produção</SelectItem>
                      <SelectItem value="2">Homologação (Testes)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Série NFC-e *</Label>
                  <Input
                    type="number"
                    min={1}
                    value={config.serie_nfce}
                    onChange={(e) => setConfig({ ...config, serie_nfce: parseInt(e.target.value) || 1 })}
                    data-testid="nfce-serie"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>ID do CSC *</Label>
                  <Input
                    value={config.csc_id}
                    onChange={(e) => setConfig({ ...config, csc_id: e.target.value })}
                    placeholder="1 ou 2"
                    data-testid="nfce-csc-id"
                  />
                  <p className="text-xs text-slate-500">Código de Segurança do Contribuinte (obtido na SEFAZ)</p>
                </div>
                <div className="space-y-2">
                  <Label>Token CSC *</Label>
                  <Input
                    type="password"
                    value={config.csc_token === '***' ? '' : config.csc_token}
                    onChange={(e) => setConfig({ ...config, csc_token: e.target.value })}
                    placeholder={config.csc_token === '***' ? '••••••••••' : 'Token CSC'}
                    data-testid="nfce-csc-token"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Próximo Número NFC-e</Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    min={1}
                    value={config.numero_nfce}
                    onChange={(e) => setConfig({ ...config, numero_nfce: parseInt(e.target.value) || 1 })}
                    data-testid="nfce-numero"
                    className="flex-1"
                  />
                  <Button 
                    onClick={handleCleanRejected}
                    disabled={cleaningRejected}
                    variant="outline"
                    className="gap-1 text-red-600 border-red-200 hover:bg-red-50 whitespace-nowrap"
                    data-testid="nfce-clean-rejected-btn"
                  >
                    {cleaningRejected ? <Loader2 className="animate-spin" size={14} /> : <Trash2 size={14} />}
                    Limpar Rejeitadas
                  </Button>
                </div>
                <p className="text-xs text-slate-500">Este número será usado na próxima NFC-e emitida. Use "Limpar Rejeitadas" para resolver erros de duplicidade.</p>
              </div>

              <div className="flex gap-4">
                <Button onClick={handleSaveConfig} disabled={saving} className="gap-2">
                  {saving ? <Loader2 className="animate-spin" size={16} /> : <Settings size={16} />}
                  Salvar Configurações
                </Button>

                <Button 
                  onClick={handleCheckSefazStatus} 
                  disabled={checkingStatus}
                  variant="outline"
                  className="gap-2"
                >
                  {checkingStatus ? (
                    <Loader2 className="animate-spin" size={16} />
                  ) : sefazStatus?.online ? (
                    <Wifi className="text-green-600" size={16} />
                  ) : (
                    <WifiOff className="text-slate-400" size={16} />
                  )}
                  Verificar Status SEFAZ
                </Button>
              </div>

              {sefazStatus && (
                <Alert className={sefazStatus.online ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}>
                  {sefazStatus.online ? (
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-red-600" />
                  )}
                  <AlertTitle className={sefazStatus.online ? 'text-green-800' : 'text-red-800'}>
                    {sefazStatus.online ? 'SEFAZ Online' : 'SEFAZ Offline'}
                  </AlertTitle>
                  <AlertDescription className={sefazStatus.online ? 'text-green-700' : 'text-red-700'}>
                    <p><strong>Status:</strong> {sefazStatus.cStat} - {sefazStatus.motivo || sefazStatus.erro}</p>
                    <p><strong>Ambiente:</strong> {sefazStatus.ambiente}</p>
                  </AlertDescription>
                </Alert>
              )}

              <Alert className="border-amber-200 bg-amber-50">
                <AlertCircle className="h-5 w-5 text-amber-600" />
                <AlertTitle className="text-amber-800">Importante</AlertTitle>
                <AlertDescription className="text-amber-700">
                  <p>Para obter o CSC (Código de Segurança do Contribuinte), acesse o portal da SEFAZ-AM:</p>
                  <a 
                    href="https://online.sefaz.am.gov.br" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    https://online.sefaz.am.gov.br
                  </a>
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notas Emitidas */}
        <TabsContent value="notas">
          <Card>
            <CardHeader>
              <CardTitle>NFC-e Emitidas</CardTitle>
              <CardDescription>Histórico de notas fiscais emitidas</CardDescription>
            </CardHeader>
            <CardContent>
              {loadingNfces ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="animate-spin text-blue-600" size={24} />
                </div>
              ) : nfces.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <FileText size={48} className="mx-auto mb-4 text-slate-300" />
                  <p>Nenhuma NFC-e emitida ainda</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {nfces.map((nfce) => (
                    <div 
                      key={nfce.id} 
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border"
                    >
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">NFC-e #{nfce.numero}</span>
                          <Badge variant={
                            nfce.status === 'autorizada' ? 'default' :
                            nfce.status === 'pendente' ? 'secondary' :
                            nfce.status === 'rejeitada' ? 'destructive' : 'outline'
                          } className={nfce.status === 'autorizada' ? 'bg-green-100 text-green-800' : ''}>
                            {nfce.status}
                          </Badge>
                          {nfce.protocolo && (
                            <span className="text-xs text-slate-500">Prot: {nfce.protocolo}</span>
                          )}
                        </div>
                        <p className="text-sm text-slate-500">
                          {formatDate(nfce.created_at)} • {formatCurrency(nfce.valor_total)}
                        </p>
                        <p className="text-xs text-slate-400 font-mono">{nfce.chave}</p>
                        {nfce.motivo && nfce.status !== 'autorizada' && (
                          <p className="text-xs text-red-500">{nfce.motivo}</p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        {nfce.status === 'pendente' && (
                          <Button 
                            variant="default" 
                            size="sm"
                            onClick={() => handleSendNfce(nfce.id)}
                            disabled={sendingNfce[nfce.id]}
                            className="gap-1 bg-blue-600 hover:bg-blue-700"
                          >
                            {sendingNfce[nfce.id] ? (
                              <Loader2 className="animate-spin" size={14} />
                            ) : (
                              <Send size={14} />
                            )}
                            Enviar SEFAZ
                          </Button>
                        )}
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleDownloadXml(nfce.id, nfce.chave)}
                          className="gap-1"
                        >
                          <Download size={14} />
                          XML
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-4 pt-4 border-t">
                <Button 
                  variant="outline" 
                  onClick={fetchNfces}
                  className="gap-2"
                >
                  <RefreshCw size={16} />
                  Atualizar Lista
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default NFCeConfig;
